import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Label,
  Modal,
  Pagination,
  Table,
} from 'semantic-ui-react';
import { API, showError, showSuccess } from '../helpers';
import { ITEMS_PER_PAGE } from '../constants';

const LOAD_STATUS_TEXT = {
  idle: '空闲',
  busy: '满载',
  degraded: '降级',
  draining: '退出中',
};

const emptyForm = { id: 0, name: '' };

const OcrNodesTable = () => {
  const [nodes, setNodes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [tokenValue, setTokenValue] = useState('');

  const loadNodes = async (page = 1, kw = keyword) => {
    setLoading(true);
    let url = `/api/ocr_node/?p=${page - 1}`;
    if (kw) {
      url += `&keyword=${encodeURIComponent(kw)}`;
    }
    const res = await API.get(url);
    const { success, message, data, total: count } = res.data;
    if (success) {
      setNodes(data || []);
      setTotal(count || 0);
      setActivePage(page);
    } else {
      showError(message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadNodes(1).catch((reason) => showError(reason));
  }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (node) => {
    setForm({ id: node.id, name: node.name || '' });
    setModalOpen(true);
  };

  const submit = async () => {
    if (!form.name) {
      showError('节点名称不能为空');
      return;
    }
    setSaving(true);
    const res = form.id
      ? await API.put('/api/ocr_node/', { id: form.id, name: form.name })
      : await API.post('/api/ocr_node/', { name: form.name });
    const { success, message, data } = res.data;
    if (success) {
      showSuccess(message || '保存成功');
      setModalOpen(false);
      if (!form.id && data && data.token) {
        setTokenValue(data.token);
        setTokenModalOpen(true);
      }
      await loadNodes(activePage);
    } else {
      showError(message);
    }
    setSaving(false);
  };

  const toggleEnabled = async (node) => {
    const res = await API.put('/api/ocr_node/', {
      id: node.id,
      enabled: !node.enabled,
    });
    const { success, message } = res.data;
    if (success) {
      showSuccess(node.enabled ? '已禁用' : '已启用');
      await loadNodes(activePage);
    } else {
      showError(message);
    }
  };

  const toggleDrain = async (node) => {
    const action = node.drain ? 'enable' : 'drain';
    const res = await API.post(`/api/ocr_node/${node.id}/${action}`);
    const { success, message } = res.data;
    if (success) {
      showSuccess(message || '已更新');
      await loadNodes(activePage);
    } else {
      showError(message);
    }
  };

  const resetToken = async (node) => {
    if (
      !window.confirm(
        `确定重置节点 ${node.node_id} 的令牌吗？旧令牌将立即失效，节点需要重新配置。`
      )
    ) {
      return;
    }
    const res = await API.post(`/api/ocr_node/${node.id}/reset_token`);
    const { success, message, data } = res.data;
    if (success) {
      showSuccess(message || '令牌已重置');
      if (data && data.token) {
        setTokenValue(data.token);
        setTokenModalOpen(true);
      }
      await loadNodes(activePage);
    } else {
      showError(message);
    }
  };

  const remove = async (node) => {
    if (!window.confirm(`确定删除节点 ${node.node_id} 吗？`)) {
      return;
    }
    const res = await API.delete(`/api/ocr_node/${node.id}`);
    const { success, message } = res.data;
    if (success) {
      showSuccess(message || '删除成功');
      await loadNodes(activePage);
    } else {
      showError(message);
    }
  };

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(tokenValue);
      showSuccess('已复制到剪贴板');
    } catch (e) {
      showError('复制失败，请手动选中复制');
    }
  };

  return (
    <>
      <Form>
        <Form.Group>
          <Form.Field width={6}>
            <Input
              placeholder='按 node_id / 版本 / 平台搜索'
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  loadNodes(1).catch((reason) => showError(reason));
                }
              }}
            />
          </Form.Field>
          <Button
            type='button'
            loading={loading}
            onClick={() => loadNodes(1).catch((reason) => showError(reason))}
          >
            查询
          </Button>
          <Button type='button' color='green' onClick={openCreate}>
            新增节点
          </Button>
        </Form.Group>
      </Form>
      <Table basic={'very'} compact size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>ID</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>节点 ID</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>名称</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>在线</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>状态</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>负载</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>平台 / 后端</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>版本</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>最后心跳</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {nodes.map((node) => (
            <Table.Row key={node.id}>
              <Table.Cell>{node.id}</Table.Cell>
              <Table.Cell>
                <code style={{ fontSize: '12px' }}>{node.node_id}</code>
              </Table.Cell>
              <Table.Cell>{node.name || '-'}</Table.Cell>
              <Table.Cell>
                <Label basic color={node.online ? 'green' : 'grey'}>
                  {node.online ? '在线' : '离线'}
                </Label>
              </Table.Cell>
              <Table.Cell>
                {node.revoked ? (
                  <Label basic color='red'>已吊销</Label>
                ) : node.enabled ? (
                  <Label basic color='green'>启用</Label>
                ) : (
                  <Label basic color='grey'>禁用</Label>
                )}
                {node.drain ? (
                  <Label basic color='orange'>维护</Label>
                ) : null}
              </Table.Cell>
              <Table.Cell>{LOAD_STATUS_TEXT[node.load_status] || node.load_status || '-'}</Table.Cell>
              <Table.Cell>
                <div style={{ fontSize: '12px' }}>
                  {node.platform || '-'} / {node.arch || '-'}
                  <br />
                  {(node.backends || []).join(', ') || '-'}
                  {node.concurrency ? ` ×${node.concurrency}` : ''}
                </div>
              </Table.Cell>
              <Table.Cell>{node.version || '-'}</Table.Cell>
              <Table.Cell>{node.last_heartbeat || '-'}</Table.Cell>
              <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                <Button size='mini' basic onClick={() => openEdit(node)}>
                  改名
                </Button>
                <Button size='mini' basic onClick={() => toggleEnabled(node)}>
                  {node.enabled ? '禁用' : '启用'}
                </Button>
                <Button size='mini' basic onClick={() => toggleDrain(node)}>
                  {node.drain ? '恢复派单' : '维护'}
                </Button>
                <Button size='mini' basic onClick={() => resetToken(node)}>
                  重置令牌
                </Button>
                <Button size='mini' basic color='red' onClick={() => remove(node)}>
                  删除
                </Button>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan='10'>
              <Pagination
                floated='right'
                activePage={activePage}
                onPageChange={(e, { activePage: page }) =>
                  loadNodes(page).catch((reason) => showError(reason))
                }
                size='small'
                siblingRange={1}
                totalPages={Math.max(1, Math.ceil(total / ITEMS_PER_PAGE))}
              />
              <span style={{ color: '#8a919f' }}>
                共 {total} 个节点（节点令牌在新建/重置时仅展示一次，请立即交付节点并妥善保存）
              </span>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size='small'>
        <Modal.Header>{form.id ? '修改节点名称' : '新增算力节点'}</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>节点名称（便于识别，如「本地 M4 算力」/「服务器 CPU OCR」）</label>
              <Input
                placeholder='请输入节点名称'
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Form.Field>
            {!form.id ? (
              <div style={{ color: '#8a919f', fontSize: '12px' }}>
                保存后将生成 node_id 与节点令牌（明文仅展示一次），请交付节点方写入配置。
              </div>
            ) : null}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setModalOpen(false)}>取消</Button>
          <Button color='green' loading={saving} onClick={submit}>
            保存
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal open={tokenModalOpen} onClose={() => setTokenModalOpen(false)} size='small'>
        <Modal.Header>节点令牌（仅展示一次，请立即保存）</Modal.Header>
        <Modal.Content>
          <Input
            fluid
            readOnly
            value={tokenValue}
            action={{ content: '复制', onClick: copyToken }}
          />
          <div style={{ color: '#c0392b', fontSize: '12px', marginTop: 8 }}>
            关闭本窗口后无法再次查看；丢失请使用「重置令牌」（旧令牌将立即失效）。
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button color='green' onClick={() => setTokenModalOpen(false)}>
            我已保存
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default OcrNodesTable;
